import { RoadSegment, RoadType, Coordinate } from './types';

export class RoadEngine {
  private roads: Record<string, RoadSegment> = {};

  constructor(roads: Record<string, RoadSegment> = {}) {
    this.roads = roads;
  }

  public addRoad(x: number, y: number, type: RoadType): RoadSegment | null {
    const key = `${x},${y}`;
    if (this.roads[key]) return null;

    const road: RoadSegment = {
      id: `road_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      x,
      y,
      type,
      autoTilingId: 'road_0'
    };

    this.roads[key] = road;
    this.recalculateTilingForCellAndNeighbors(x, y);
    return road;
  }

  public removeRoad(x: number, y: number): boolean {
    const key = `${x},${y}`;
    if (!this.roads[key]) return false;

    delete this.roads[key];

    const directions = [
      { dx: 0, dy: -1 },
      { dx: 1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: -1, dy: 0 }
    ];

    for (const dir of directions) {
      this.recalculateTilingForCell(x + dir.dx, y + dir.dy);
    }
    return true;
  }

  public hasRoad(x: number, y: number): boolean {
    return !!this.roads[`${x},${y}`];
  }

  public getRoads(): Record<string, RoadSegment> {
    return this.roads;
  }

  public recalculateTilingForCellAndNeighbors(x: number, y: number) {
    this.recalculateTilingForCell(x, y);
    this.recalculateTilingForCell(x, y - 1); // North
    this.recalculateTilingForCell(x + 1, y); // East
    this.recalculateTilingForCell(x, y + 1); // South
    this.recalculateTilingForCell(x - 1, y); // West
  }

  private recalculateTilingForCell(x: number, y: number) {
    const key = `${x},${y}`;
    const road = this.roads[key];
    if (!road) return;

    let mask = 0;
    if (this.hasRoad(x, y - 1)) mask |= 1;  // North
    if (this.hasRoad(x + 1, y)) mask |= 2;  // East
    if (this.hasRoad(x, y + 1)) mask |= 4;  // South
    if (this.hasRoad(x - 1, y)) mask |= 8;  // West

    road.autoTilingId = `road_tile_${mask}`;
  }

  /**
   * Standard A* Pathfinding on road network coordinates
   */
  public findPath(startX: number, startY: number, endX: number, endY: number): Coordinate[] | null {
    const startKey = `${startX},${startY}`;
    const endKey = `${endX},${endY}`;

    if (!this.roads[startKey] || !this.roads[endKey]) return null;
    if (startKey === endKey) return [{ x: startX, y: startY }];

    const directions = [
      { dx: 0, dy: -1 },
      { dx: 1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: -1, dy: 0 }
    ];

    interface Node {
      key: string;
      x: number;
      y: number;
      g: number;
      h: number;
      f: number;
      parent: Node | null;
    }

    const openList: Node[] = [];
    const closedList = new Set<string>();

    openList.push({
      key: startKey,
      x: startX,
      y: startY,
      g: 0,
      h: Math.abs(startX - endX) + Math.abs(startY - endY),
      f: Math.abs(startX - endX) + Math.abs(startY - endY),
      parent: null
    });

    while (openList.length > 0) {
      openList.sort((a, b) => a.f - b.f);
      const current = openList.shift()!;
      closedList.add(current.key);

      if (current.key === endKey) {
        const path: Coordinate[] = [];
        let curr: Node | null = current;
        while (curr !== null) {
          path.unshift({ x: curr.x, y: curr.y });
          curr = curr.parent;
        }
        return path;
      }

      for (const dir of directions) {
        const nx = current.x + dir.dx;
        const ny = current.y + dir.dy;
        const nKey = `${nx},${ny}`;

        if (!this.roads[nKey] || closedList.has(nKey)) continue;

        const gScore = current.g + 1;
        const hScore = Math.abs(nx - endX) + Math.abs(ny - endY);
        const fScore = gScore + hScore;

        const existingOpen = openList.find(n => n.key === nKey);
        if (existingOpen) {
          if (gScore < existingOpen.g) {
            existingOpen.g = gScore;
            existingOpen.f = fScore;
            existingOpen.parent = current;
          }
        } else {
          openList.push({
            key: nKey,
            x: nx,
            y: ny,
            g: gScore,
            h: hScore,
            f: fScore,
            parent: current
          });
        }
      }
    }
    return null;
  }
}
